import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { X, ArrowRight, ArrowLeft, PlayCircle } from "lucide-react";

/* Tour steps. `tab` tells the host app to switch tabs before showing. */
export const TOUR_STEPS = [
  { id: 0, tab: "design", target: "input-business-name",
    title: "1. Merchant name",
    body: "Start by typing the merchant's business name here. It'll appear on the cover and throughout the PDF." },
  { id: 1, tab: "design", target: "input-rep-name",
    title: "2. Your name",
    body: "This is the rep / broker name shown on the cover and contact card. Add your phone & email below it." },
  { id: 2, tab: "offers", target: "tab-offers",
    title: "3. Offers area",
    body: "This tab is where you build your list of funding offers." },
  { id: 3, tab: "offers", target: "drop-zone",
    title: "4. Upload offers",
    body: "Drop screenshots, PDFs, or a CSV — the AI reads them and extracts each offer's term, funding, payment, factor, fees, etc. Multiple files at once is fine." },
  { id: 4, tab: "offers", target: "add-offer-btn",
    title: "5. Add manually",
    body: "Prefer to type? Click here to add an empty offer card. APR, MRR, and factor rate auto-calculate as you fill in funded amount, payback, and term." },
  { id: 5, tab: "offers", target: "toggle-show_apr",
    title: "6. Show / hide fields",
    body: "Untick any field here and it disappears across the entire PDF — comparison table, offer cards, and summary stats." },
  { id: 6, tab: null, target: "chat-input",
    title: "7. AI edits",
    body: "Type things like 'make it navy', 'recommend offer 13', 'hide APR', 'add a note to offer 2'. Claude rewrites the PDF instantly." },
  { id: 7, tab: null, target: "download-pdf-btn",
    title: "8. Download",
    body: "When you're happy, hit Download PDF — that's your finished proposal." },
];

export function useTour() {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem("tour_seen")) setActive(true);
  }, []);

  const start  = () => { setStep(0); setActive(true); };
  const finish = () => { localStorage.setItem("tour_seen", "1"); setActive(false); };
  const next   = () => setStep(s => Math.min(s + 1, TOUR_STEPS.length - 1));
  const prev   = () => setStep(s => Math.max(s - 1, 0));

  return { active, step, start, finish, next, prev };
}

export function Tour({ active, step, onNext, onPrev, onSkip, onFinish, onTabChange }) {
  const stepDef = TOUR_STEPS[step];
  const [rect, setRect] = useState(null);
  const [tip, setTip] = useState({ left: 0, top: 0, side: "bottom" });
  const rafRef = useRef(0);

  // switch the host tab when needed
  useEffect(() => {
    if (!active || !stepDef) return;
    if (stepDef.tab) onTabChange?.(stepDef.tab);
  }, [active, step]);

  // measure target every frame for ~600ms after tab switch
  useLayoutEffect(() => {
    if (!active || !stepDef) return;
    let cancelled = false;
    let frames = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-testid="${stepDef.target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
        const pad = 12;
        const tipW = 320;
        let side = "right";
        let left = r.right + pad;
        let top  = r.top;
        if (left + tipW > window.innerWidth - 20) {
          // doesn't fit right -> place below
          side = "bottom";
          left = Math.max(20, Math.min(r.left, window.innerWidth - tipW - 20));
          top  = r.bottom + pad;
        }
        if (top + 200 > window.innerHeight) {
          // doesn't fit below -> place above
          side = "top";
          top = Math.max(20, r.top - 200 - pad);
        }
        setTip({ left, top, side });
      }
      frames += 1;
      if (frames < 40) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    const onResize = () => tick();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [active, step]);

  if (!active || !stepDef) return null;

  const last = step === TOUR_STEPS.length - 1;
  const progress = ((step + 1) / TOUR_STEPS.length) * 100;

  return (
    <div data-testid="tour-overlay" style={{ position: "fixed", inset: 0, zIndex: 9999, pointerEvents: "none" }}>
      {/* dim background using 4 panels around the highlight to avoid covering it */}
      {rect && (
        <>
          <div style={dimStyle(0, 0, "100vw", rect.top - 6)} />
          <div style={dimStyle(0, rect.top - 6, rect.left - 6, rect.height + 12)} />
          <div style={dimStyle(rect.left + rect.width + 6, rect.top - 6, "100vw", rect.height + 12)} />
          <div style={dimStyle(0, rect.top + rect.height + 6, "100vw", "100vh")} />
          {/* highlight ring */}
          <div style={{
            position: "fixed",
            left: rect.left - 6, top: rect.top - 6,
            width: rect.width + 12, height: rect.height + 12,
            border: "2px solid #60A5FA",
            borderRadius: 10,
            boxShadow: "0 0 0 3px rgba(96,165,250,0.25), 0 12px 28px -10px rgba(59,130,246,0.6)",
            pointerEvents: "none",
            transition: "all 180ms ease",
          }} />
        </>
      )}

      {/* Tooltip card */}
      <div data-testid="tour-tooltip" style={{
        position: "fixed",
        left: tip.left, top: tip.top,
        width: 320,
        background: "linear-gradient(180deg,#161B23 0%,#1B212B 100%)",
        border: "1px solid #262D38",
        borderRadius: 14,
        padding: "1rem 1.05rem 0.85rem",
        boxShadow: "0 22px 48px -12px rgba(0,0,0,0.8)",
        pointerEvents: "auto",
        color: "#E5E9F0",
        fontFamily: "inherit",
      }}>
        {/* progress + skip */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#60A5FA", textTransform: "uppercase" }}>
            Tour · {step + 1} / {TOUR_STEPS.length}
          </span>
          <button onClick={onSkip} data-testid="tour-skip-btn"
                  style={{ background: "transparent", border: "none", color: "#8A93A3", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <X size={14}/> Skip tour
          </button>
        </div>

        <div style={{ fontWeight: 700, fontSize: "1.0rem", marginBottom: 6 }}>{stepDef.title}</div>
        <div style={{ fontSize: "0.86rem", lineHeight: 1.45, color: "#C9D1DC", marginBottom: 14 }}>
          {stepDef.body}
        </div>

        {/* progress bar */}
        <div style={{ height: 4, background: "#262D38", borderRadius: 99, overflow: "hidden", marginBottom: 10 }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(90deg,#3B82F6,#60A5FA)", transition: "width 220ms ease" }} />
        </div>

        <div style={{ display: "flex", gap: 6, justifyContent: "space-between" }}>
          <button onClick={onPrev} disabled={step === 0} data-testid="tour-prev-btn"
                  style={btnStyle(step === 0 ? "ghost-dim" : "ghost")}>
            <ArrowLeft size={14}/> Back
          </button>
          {last ? (
            <button onClick={onFinish} data-testid="tour-finish-btn" style={btnStyle("primary")}>
              Got it! <PlayCircle size={14}/>
            </button>
          ) : (
            <button onClick={onNext} data-testid="tour-next-btn" style={btnStyle("primary")}>
              Next <ArrowRight size={14}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function dimStyle(left, top, width, height) {
  return {
    position: "fixed",
    left, top, width, height,
    background: "rgba(8,10,14,0.62)",
    backdropFilter: "blur(1px)",
    pointerEvents: "auto",
  };
}

function btnStyle(kind) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "0.45rem 0.85rem", borderRadius: 8,
    fontWeight: 700, fontSize: "0.82rem", cursor: "pointer",
    border: "1px solid transparent",
    fontFamily: "inherit",
  };
  if (kind === "primary") return { ...base, background: "#3B82F6", color: "white", boxShadow: "0 4px 14px -4px rgba(59,130,246,0.5)" };
  if (kind === "ghost")   return { ...base, background: "transparent", color: "#E5E9F0", borderColor: "#262D38" };
  return { ...base, background: "transparent", color: "#5B6473", borderColor: "#1B212B", cursor: "default" };
}
